<template>
  <transition
    name="fade-slide-down"
    appear
  >
    <div
      :class="$style.base"
      ref="popoverEl"
      :style="popoverPosition"
    >
      <div
        :class="$style.arrow"
        :style="arrowPosition"
      />
      <div :class="$style.header">
        <BaseHeading>{{ heading }}</BaseHeading>
        <BaseIcon
          name="close"
          @click="emit('close')"
        />
      </div>
      <div :class="$style.center">
        <slot />
      </div>
      <div :class="$style.footer">
        <slot name="footer" />
      </div>
    </div>
  </transition>
</template>

<script
  lang="ts"
  setup
>
import { onClickOutside } from '@vueuse/core';
import { onMounted, onUnmounted, ref } from 'vue';

const props = defineProps<{
  heading: string;
  positionOn: HTMLElement;
}>();
const emit = defineEmits(['close']);

const popoverEl = ref<HTMLDivElement>();

onClickOutside(
  popoverEl,
  () => {
    emit('close');
  },
  {
    ignore: [props.positionOn],
  },
);

const popoverPosition = ref<{ left?: string; right?: string; top?: string }>(
  {},
);
const arrowPosition = ref<{ left?: string; right?: string }>({});
const arrowWidth = 14;
const verticalOffset = 12;
const scrollListenerOptions = { capture: true, passive: true };

const setPosition = () => {
  if (!popoverEl.value) return;

  const popoverRect = popoverEl.value.getBoundingClientRect();
  const positionOnRect = props.positionOn.getBoundingClientRect();

  const popoverWidth = popoverRect.width;

  // The popover is aligned to the left by default.
  // Check the intended left-aligned position, not the element's current rect.
  const alignRight = positionOnRect.left + popoverWidth > window.innerWidth;

  // When aligning to the right, but the popover does not fit in the viewport, align it to the center instead
  const alignCenter = alignRight && positionOnRect.right - popoverWidth < 0;

  if (alignCenter) {
    const left = (window.innerWidth - popoverWidth) / 2;
    popoverPosition.value.left = `${left}px`;
    popoverPosition.value.right = 'auto';
    arrowPosition.value.left = `${(positionOnRect.left + positionOnRect.width / 2) - left - arrowWidth / 2}px`;
    arrowPosition.value.right = 'auto';
  } else if (alignRight) {
    popoverPosition.value.right = `${window.innerWidth - positionOnRect.right}px`;
    popoverPosition.value.left = 'auto';
    arrowPosition.value.right = `${positionOnRect.width / 2 - arrowWidth / 2}px`;
    arrowPosition.value.left = 'auto';
  } else {
    popoverPosition.value.left = `${positionOnRect.left}px`;
    popoverPosition.value.right = 'auto';
    arrowPosition.value.left = `${positionOnRect.width / 2 - arrowWidth / 2}px`;
    arrowPosition.value.right = 'auto';
  }

  popoverPosition.value.top = `${positionOnRect.top + positionOnRect.height + verticalOffset}px`;
};

onMounted(() => {
  setPosition();

  window.addEventListener('resize', setPosition);
  window.addEventListener('scroll', setPosition, scrollListenerOptions);
});

onUnmounted(() => {
  window.removeEventListener('resize', setPosition);
  window.removeEventListener('scroll', setPosition, scrollListenerOptions);
});
</script>

<style
  lang="scss"
  module
>
.base {
  position: fixed;
  background-color: var(--surface-primary);
  box-shadow: var(--modal-shadow);
  border-radius: $radius;
  width: 370px;
  max-width: calc(100vw - #{$spacing * 2});
}

.arrow {
  position: absolute;
  top: -7px;
  border-left: 7px solid transparent; // Dont change this value without changing the arrowWidth variable
  border-right: 7px solid transparent; // Dont change this value without changing the arrowWidth variable
  border-bottom: 7px solid var(--surface-primary); // Dont change this value without changing the arrowWidth variable
}

.header {
  display: flex;
  justify-content: space-between;
  padding: $spacing;
  border-bottom: 1px solid var(--border);

  &:empty {
    display: none;
  }
}

.center {
  padding: $spacing;
  max-height: 400px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.footer {
  padding: $spacing;
  border-top: 1px solid var(--border);

  &:empty {
    display: none;
  }
}
</style>
